// #6847 — restoring log entries the conflict resolver closed, from this device's own archive.
//
// Before #6825 and #6834 the resolver (#4314 Phase C) could close the copy of a log entry that
// held the user's data while an empty `Scheduled` placeholder stayed the copy everyone sees.
// Real case: user Zn68Plam's weigh-in of 3 Jul 2026. A Mac saved an empty placeholder, CouchDB
// showed it, and the Mac's resolver closed the iPhone's `Done` copy beside it. Compaction has
// since removed that copy from the server and the nightly exports never held it (#6836). The one
// copy left is the one this device archived before closing it.
//
// This step reads the archive and asks the rule the #6836 server repair used,
// `decideCloseOnlyRestore` (shared/src/data-model/resolver-repair-rules.ts), what to do with each
// archived copy, the archived copy standing in for the export copy. Only `RestoreWhole` writes:
// the archived copy, whole, as a new revision on top of the shown one. Everything else is only
// counted, including the #6829 merge shape, whose repair is a separate decision.
//
// Each archive row is judged once; the outcome is written onto its index row. Until then the
// archive sweep's 30-day rule keeps the row (`isRestoreJudged`), because switching the resolver
// back on (#6830) would otherwise delete the copy first.
//
// Extracted from DatabaseWrapper and dependency-injected like the resolver beside it, so it runs
// under node in tests. See design-docs/sync-integrity.md, "Restoring from the archive (#6847)".

import { describeErrorForLog } from '@cf-shared/error-describe';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isLogRecordEntry } from '@cf-shared/data-model/user-log';
import { decideCloseOnlyRestore, RepairOutcome } from '@cf-shared/data-model/resolver-repair-rules';
import { DateTime } from 'luxon';
import {
  ConflictArchiveDeps,
  ConflictArchiveIndexEntry,
  ConflictArchiveRowMarks,
  isRestoreJudged,
  markConflictArchiveRowsAsync,
  readConflictArchiveRowsAsync,
} from './database-wrapper-conflict-archive';
import { HeldCopy } from './database-wrapper-conflict-resolve';
import { ConflictRunConditions, ConflictRunStep, decideConflictRunStep, CONFLICT_RESOLVE_YIELD_MS } from './sync-conflict-resolve-decisions';

/** The database calls the restore step needs, injected so it is node-testable. */
export interface ConflictArchiveRestoreDeps {
  /** The archive's own injected `_local/` calls. */
  archive: ConflictArchiveDeps;
  /** Every leaf of one document with its revision history: `get(id, {open_revs: 'all', revs: true})`. */
  readLeavesAsync: (docId: string) => Promise<any[]>;
  /** Write the restored copy. A plain put, so a winner that moved underneath is refused with a 409. */
  putDocAsync: (doc: any) => Promise<{ rev: string }>;
  /** The live copy the app holds of this document, if any. A restore defers while one exists. */
  heldCopy: (docId: string) => HeldCopy | undefined;
  /** Run one document's restore inside the write mutex. */
  withWriteLock: <T>(work: () => Promise<T>) => Promise<T>;
  /** Re-read the live abort conditions — checked between EVERY document. */
  readRunConditions: () => Pick<ConflictRunConditions,
    'appVisible' | 'online' | 'signedIn' | 'impersonating' | 'shuttingDown' | 'databaseRecreating'>;
  /** Called after a restored copy lands, so the caller can refresh the identity map and record
   *  the revision as locally authored, exactly as after a resolver merge. */
  onWinnerWritten: (docId: string, newRev: string, body: any) => void;
  /** Epoch milliseconds. Injected so tests control time. */
  nowMs: () => number;
  /** Pause between documents so user saves can take the write mutex. */
  yieldAsync: (ms: number) => Promise<void>;
  /** This device's id: the device half of the restored copy's stamp. */
  deviceId: string;
  /** Surface a per-document failure. Nothing is ever swallowed. */
  reportFailure: (docId: string, stage: string, err: unknown) => void;
}

/** Outcomes this step stores that the shared rule does not name. */
export enum ArchiveRestoreLocalOutcome {
  /** The archived copy was written back as the shown copy. */
  Restored = 'restored',
  /** The archived copy is not a log entry; only log entries are restored. */
  NotLogEntry = 'not-log-entry',
  /** The index row points at an archive entry that is not there (a half-written archive). */
  EntryMissing = 'entry-missing',
}

/** What one restore pass did, shaped for the `sync_conflict_archive_restore` event. */
export interface ConflictArchiveRestoreResult {
  /** Archived copies judged this pass (rows, not documents). */
  judgedRowCount: number;
  restoredDocCount: number;
  /** The #6829 merge shape: a repair the rule would write, which this step only counts. */
  mergeShapeDocCount: number;
  reportOnlyDocCount: number;
  notDamagedDocCount: number;
  unreadableDocCount: number;
  otherDocCount: number;
  /** Restores put off because the app holds the document; retried next pass. */
  deferredDocCount: number;
  /** Rows still not judged when the pass ended. While this is above 0 the sweep keeps them. */
  pendingRowCount: number;
  restoredDocTypes: string[];
  durationMs: number;
  /** True when the pass judged at least one row; drives the resume marker. */
  madeProgress: boolean;
}

/** What one document's turn concluded. */
enum DocTurn {
  /** Judged: every row of the document gets this outcome. */
  Judged = 'Judged',
  /** Left for a later pass: held by the app, unreadable for the first time, or a failure. */
  Retry = 'Retry',
}

type DocTurnResult = { turn: DocTurn.Judged; outcome: string } | { turn: DocTurn.Retry; firstUnreadable?: boolean };

/** Which counter an outcome belongs to. Kept beside the outcomes so a new one cannot go uncounted silently. */
function countOutcome(result: ConflictArchiveRestoreResult, outcome: string): void {
  switch (outcome) {
    case ArchiveRestoreLocalOutcome.Restored: result.restoredDocCount++; break;
    case RepairOutcome.RestoreFromExport:
    case RepairOutcome.StateToDone: result.mergeShapeDocCount++; break;
    case RepairOutcome.ReportOnly:
    case RepairOutcome.ManualLook: result.reportOnlyDocCount++; break;
    case RepairOutcome.NotDamaged:
    case RepairOutcome.AlreadyRepaired:
    case RepairOutcome.ChangedSince: result.notDamagedDocCount++; break;
    case RepairOutcome.Unreadable: result.unreadableDocCount++; break;
    default: result.otherDocCount++; break;
  }
}

/**
 * Judge, and where the rule says so restore, ONE document from its archived copies. Called inside
 * the write mutex. Only `RestoreWhole` writes; it is put off while the app holds the document,
 * because the write refreshes the identity map onto the held object.
 */
async function restoreOneDocAsync(
  deps: ConflictArchiveRestoreDeps,
  docId: string,
  rows: ConflictArchiveIndexEntry[],
  result: ConflictArchiveRestoreResult,
): Promise<DocTurnResult> {
  // read every archived copy of this document; a row whose entry is gone has nothing to restore
  const archivedCopies: any[] = [];
  for (const row of rows) {
    let entry: any;
    try {
      entry = await deps.archive.getLocal(row.key);
    } catch (err) {
      if (isPouchNotFound(err)) continue;
      deps.reportFailure(docId, 'read-archive-entry', err);
      return { turn: DocTurn.Retry };
    }
    if (entry?.body && typeof entry.body === 'object') archivedCopies.push(entry.body);
  }
  if (archivedCopies.length === 0) return { turn: DocTurn.Judged, outcome: ArchiveRestoreLocalOutcome.EntryMissing };
  if (!archivedCopies.some(copy => isLogRecordEntry(copy))) {
    return { turn: DocTurn.Judged, outcome: ArchiveRestoreLocalOutcome.NotLogEntry };
  }

  let leaves: any[];
  try {
    leaves = await deps.readLeavesAsync(docId);
  } catch (err) {
    // if the document is not in this database at all, there is no live copy to restore over,
    // which is the rule's own answer for a document with only deleted leaves. Retrying it would
    // report the same failure every session for ever.
    if (isPouchNotFound(err)) return { turn: DocTurn.Judged, outcome: RepairOutcome.NoResolverClose };
    deps.reportFailure(docId, 'read-leaves', err);
    return { turn: DocTurn.Retry };
  }

  // the device writes as itself, and its own saves carry the same id, so the rule's "already
  // repaired" check is off here; the index row remembering the judgement does that job
  const decision = decideCloseOnlyRestore(docId, leaves, archivedCopies, { stampDevice: deps.deviceId, repairedMarker: null });
  if (decision.outcome === RepairOutcome.Unreadable) {
    // judged only on its second sighting: a read can fail for a moment, but a #6415 leaf whose
    // content is gone fails every time and must not hold the row back for ever
    const seenBefore = rows.some(row => typeof row.restoreUnreadableSinceUtc === 'string');
    return seenBefore ? { turn: DocTurn.Judged, outcome: decision.outcome } : { turn: DocTurn.Retry, firstUnreadable: true };
  }
  if (decision.outcome !== RepairOutcome.RestoreWhole || !decision.body) {
    return { turn: DocTurn.Judged, outcome: decision.outcome };
  }

  // if the app holds this document, writing would rewrite the held object under it: try later
  if (deps.heldCopy(docId) !== undefined) {
    result.deferredDocCount++;
    return { turn: DocTurn.Retry };
  }

  let newRev: string;
  try {
    newRev = (await deps.putDocAsync(decision.body)).rev;
  } catch (err) {
    // a 409 means the shown copy moved under us; the next pass decides again on the new state
    deps.reportFailure(docId, 'put-restored-copy', err);
    return { turn: DocTurn.Retry };
  }
  // outside the try above: this is app code, and its failure is not a failed put
  try {
    deps.onWinnerWritten(docId, newRev, { ...decision.body, _rev: newRev });
  } catch (err) {
    deps.reportFailure(docId, 'refresh-identity-map', err);
  }
  const type = decision.body.type;
  if (typeof type === 'string' && !result.restoredDocTypes.includes(type)) result.restoredDocTypes.push(type);
  return { turn: DocTurn.Judged, outcome: ArchiveRestoreLocalOutcome.Restored };
}

/**
 * One restore pass over this device's conflict archive (#6847).
 *
 * Paced exactly like a resolver run: one document per write-mutex hold, a pause between
 * documents, and the same work budget and abort conditions (`decideConflictRunStep`). What a pass
 * did not reach stays unjudged, so the sweep keeps it and the next pass carries on.
 *
 * Never throws: a failure on one document is reported and the pass moves on. A failure to read or
 * write the index itself is reported once and ends the pass with nothing marked, which only means
 * the same rows are judged again next time.
 */
export async function restoreFromConflictArchiveAsync(deps: ConflictArchiveRestoreDeps): Promise<ConflictArchiveRestoreResult> {
  const result: ConflictArchiveRestoreResult = {
    judgedRowCount: 0, restoredDocCount: 0, mergeShapeDocCount: 0, reportOnlyDocCount: 0, notDamagedDocCount: 0,
    unreadableDocCount: 0, otherDocCount: 0, deferredDocCount: 0, pendingRowCount: 0,
    restoredDocTypes: [], durationMs: 0, madeProgress: false,
  };
  const startedMs = deps.nowMs();

  let rows: ConflictArchiveIndexEntry[];
  try {
    rows = await readConflictArchiveRowsAsync(deps.archive);
  } catch (err) {
    deps.reportFailure('', 'read-archive-index', err);
    return result;
  }

  // one turn per document, holding all of its unjudged rows, so the rule sees every closed copy at once
  const rowsByDoc = new Map<string, ConflictArchiveIndexEntry[]>();
  for (const row of rows) {
    if (isRestoreJudged(row)) continue;
    const docId = docIdOfArchiveKey(row.key);
    if (docId === undefined) continue;
    rowsByDoc.set(docId, [...(rowsByDoc.get(docId) ?? []), row]);
  }

  const marks: ConflictArchiveRowMarks = new Map();
  const nowIso = () => DateTime.fromMillis(deps.nowMs()).toUTC().toISO() as string;
  let workMs = 0;
  let attempted = 0;
  for (const [docId, docRows] of rowsByDoc) {
    const step = decideConflictRunStep({
      ...deps.readRunConditions(),
      workMs,
      wallMs: deps.nowMs() - startedMs,
      documentsAttempted: attempted,
    });
    if (step !== ConflictRunStep.Continue) break;
    if (attempted > 0) await deps.yieldAsync(CONFLICT_RESOLVE_YIELD_MS);
    attempted++;

    const docStartedMs = deps.nowMs();
    let turn: DocTurnResult;
    try {
      turn = await deps.withWriteLock(() => restoreOneDocAsync(deps, docId, docRows, result));
    } catch (err) {
      deps.reportFailure(docId, 'write-lock', err);
      turn = { turn: DocTurn.Retry };
    }
    workMs += deps.nowMs() - docStartedMs;

    if (turn.turn === DocTurn.Judged) {
      countOutcome(result, turn.outcome);
      const judgedUtc = nowIso();
      for (const row of docRows) marks.set(row.key, { restoreOutcome: turn.outcome, restoreJudgedUtc: judgedUtc });
      result.judgedRowCount += docRows.length;
    } else if (turn.firstUnreadable) {
      const since = nowIso();
      for (const row of docRows) marks.set(row.key, { restoreUnreadableSinceUtc: since });
    }
  }

  let marksLanded = true;
  try {
    await markConflictArchiveRowsAsync(deps.archive, marks);
  } catch (err) {
    // nothing is lost: the rows stay unjudged and the next pass judges them again. A restored
    // document is then read as holding the user's data, so it is not written twice.
    deps.reportFailure('', 'mark-archive-rows', err);
    marksLanded = false;
    result.judgedRowCount = 0;
  }

  // if the marks did not land, every row this pass judged is still unjudged on disk, so it counts as pending
  const judgedKeys = marksLanded
    ? new Set([...marks].filter(([, mark]) => mark.restoreOutcome).map(([key]) => key))
    : new Set<string>();
  result.pendingRowCount = rows.filter(row => !isRestoreJudged(row) && !judgedKeys.has(row.key)).length;
  result.madeProgress = result.judgedRowCount > 0;
  result.durationMs = workMs;
  return result;
}

/** The document id inside an archive key `_local/conflict-archive/<docId>/<rev>`, or undefined for a malformed key. */
export function docIdOfArchiveKey(key: string): string | undefined {
  const prefix = '_local/conflict-archive/';
  if (typeof key !== 'string' || !key.startsWith(prefix)) return undefined;
  const rest = key.slice(prefix.length);
  // the revision is the last segment; a document id may itself contain slashes (`log/…`)
  const lastSlash = rest.lastIndexOf('/');
  return lastSlash > 0 ? rest.slice(0, lastSlash) : undefined;
}

/** For the failure log line: a described error, never a raw object. */
export function describeRestoreFailure(docId: string, stage: string, err: unknown): string {
  return `${docId || '(archive)'} failed at ${stage}: ${describeErrorForLog(err)}`;
}
